import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { get } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { setUser } from "../../store/slices/authSlice"
import { mapApiProject } from "../Projects/ProjectsListing"
import PageHeader from "../../components/DashboardComponents/PageHeader"
import DashboardCard from "../../components/DashboardComponents/DashboardCard"
import ListItem from "../../components/DashboardComponents/ListItem"
import MiniBarChart from "../../components/DashboardComponents/MiniBarChart"
import MiniStat, {
  MiniStatRow,
} from "../../components/DashboardComponents/MiniStat"
import ProgressBar from "../../components/DashboardComponents/ProgressBar"
import { AvatarGroup } from "../../components/DashboardComponents/AvatarGroup"
import ProjectsTable from "../../components/DashboardComponents/ProjectsTable"
import CustomButton from "../../components/OnboardingComponents/CustomButton"
import EmptyState from "../../components/Common/EmptyState"
import StartSessionDropdown from "../../components/DashboardComponents/StartSessionDropdown"
import {
  AcceptInviteModal,
  AddCollaboratorModal,
  RequestChangeCityModal,
  StartShadowSessionModal,
  StartSoloSessionModal,
} from "../../components/Modals"
import { ICONS } from "../../constants/icons"
import { getUserAvatarUrl, resolveMediaUrl } from "../../helpers/userAvatar"
import { getXpProgress } from "../../helpers/xpLevel"
import {
  flushPendingLevelUpCelebrations,
} from "../../helpers/xpLevelCelebration"
import { refreshNotificationInbox } from "../../helpers/notifications"
import { isSameUserId, nameWithYou } from "../../helpers/userDisplay"
import {
  createProjectSession,
  getProjectCollaborators,
  resolveSessionCity,
  resolveSessionProject,
  resolveSessionSubtitle,
} from "../../helpers/session"
import { useSessionDetailPlayer } from "../../components/FloatingPlayer"
import { getActiveSessionSnapshot } from "../../helpers/activeSession"
import { formatDashboardGreeting } from "../../helpers/timeGreeting"
import { formatDurationFromHours } from "../../helpers/formatDuration"
import { formatSessionVisibilityLabel } from "../../helpers/sessionDetail"
import { Bounce, toast } from "react-toastify"
import {
  fetchMandatoryProjectAcceptInvite,
  isMandatoryProjectAcceptDone,
  markMandatoryProjectAcceptDone,
} from "../../helpers/mandatoryProjectAccept"
import { cityHubTabPath } from "../CityHub/cityHubTabs"

const ACTIVITY_DOT_COLORS = ["blue", "green", "purple", "orange"]

const ACTIVITY_DOT_BY_LOG = {
  loggedIn: "green",
  loggedOut: "orange",
  modelActivity: "blue",
  created: "green",
  updated: "blue",
  deleted: "orange",
}

const capitalize = (str = "") =>
  str ? str[0].toUpperCase() + str.slice(1) : ""

const ENDED_SESSION_STATUSES = new Set([
  "completed",
  "ended",
  "exited",
  "left",
  "cancelled",
  "canceled",
])

const isLiveSession = session => {
  if (!session) return false
  if (session.ended_at) return false
  const status = String(session.status || "")
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
  if (ENDED_SESSION_STATUSES.has(status)) return false
  return true
}

const normalizeCollabStatus = value =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, "_")

/** Same rule as sessions listing / join invite notifications. */
const canJoinDashboardSession = session => {
  if (!session || !isLiveSession(session)) return false
  return normalizeCollabStatus(session.current_user_collaboration_status) === "invited"
}

const formatRelativeTime = dateString => {
  if (!dateString) return ""
  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) return ""

  const diffMs = Date.now() - date.getTime()
  const diffSec = Math.max(0, Math.floor(diffMs / 1000))
  if (diffSec < 60) return "Just now"

  const diffMin = Math.floor(diffSec / 60)
  if (diffMin < 60) return `${diffMin} min ago`

  const diffHours = Math.floor(diffMin / 60)
  if (diffHours < 24) {
    return `${diffHours} ${diffHours === 1 ? "hour" : "hours"} ago`
  }

  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) {
    return `${diffDays} ${diffDays === 1 ? "day" : "days"} ago`
  }

  const diffWeeks = Math.floor(diffDays / 7)
  if (diffWeeks < 5) {
    return `${diffWeeks} ${diffWeeks === 1 ? "week" : "weeks"} ago`
  }

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

const stripHtml = value =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim()

const mapActivityLog = (log, index = 0) => {
  const eventKey = log?.properties?.event || log?.log_name
  return {
    id: log.id,
    title: stripHtml(log.description || log.log_name || "Activity"),
    time: formatRelativeTime(log.created_at),
    dotColor:
      ACTIVITY_DOT_BY_LOG[eventKey] ||
      ACTIVITY_DOT_BY_LOG[log?.log_name] ||
      ACTIVITY_DOT_COLORS[index % ACTIVITY_DOT_COLORS.length],
  }
}

const isOwnerRole = (value = "") => /^owner$/i.test(String(value).trim())

/**
 * Map project-invitation (+ nested user) → Active Collaborators row.
 * Never surface membership `role: "owner"` as the UI label — use description / role type.
 */
const mapInvitationCollaborator = (invitation, currentUserId) => {
  const user = invitation.user || invitation.invitee || {}
  const userId = user.id || invitation.user_id

  const name =
    user.full_name ||
    [user.first_name, user.last_name].filter(Boolean).join(" ").trim() ||
    user.name ||
    invitation.full_name ||
    invitation.name ||
    user.email ||
    invitation.email ||
    ""

  if (!name || isOwnerRole(name)) return null

  const roleRaw =
    invitation.role_type?.name ||
    invitation.role_types?.[0]?.name ||
    invitation.description ||
    ""
  const role = roleRaw && !isOwnerRole(roleRaw) ? String(roleRaw).trim() : ""

  return {
    id: invitation.id || userId || name,
    userId: userId || null,
    name: nameWithYou(name, isSameUserId(userId, currentUserId)),
    role,
    avatar: getUserAvatarUrl(user) || resolveMediaUrl(invitation.avatar),
  }
}

const DashboardSkeletonCard = ({ className = "" }) => (
  <div className={`dashboard__skeleton-card ${className}`} aria-hidden="true">
    <div className="dashboard__skeleton-bar dashboard__skeleton-bar--title" />
    <div className="dashboard__skeleton-bar" />
    <div className="dashboard__skeleton-bar dashboard__skeleton-bar--short" />
  </div>
)

/**
 * Dashboard — Figma 40004456-18426
 */
const Dashboard = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { openSessionDetail } = useSessionDetailPlayer()
  const dispatch = useDispatch()
  const currentUser = useSelector(state => state.auth.currentUser)
  const [initialInvite, setInitialInvite] = useState(null)
  const [isCityRequestOpen, setIsCityRequestOpen] = useState(false)
  const [isInviteOpen, setIsInviteOpen] = useState(false)
  const [sessionModal, setSessionModal] = useState(null)
  const [sessionProject, setSessionProject] = useState(null)
  const [isCreatingSession, setIsCreatingSession] = useState(false)
  const [activeProjects, setActiveProjects] = useState([])
  const [activeCollaborators, setActiveCollaborators] = useState([])
  const [invitesSummary, setInvitesSummary] = useState(null)
  const [dashboardStats, setDashboardStats] = useState(null)
  const [todayTasks, setTodayTasks] = useState([])
  const [activeSessions, setActiveSessions] = useState([])
  const [projectsSummary, setProjectsSummary] = useState(null)
  const [recentActivity, setRecentActivity] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    document.title = "Dashboard | AMOK . EARTH"
  }, [])

  // Deferred onboarding level-ups: play once on first dashboard visit.
  useEffect(() => {
    if (!currentUser?.id) return undefined

    let cancelled = false

    const run = async () => {
      // Flush anything queued during onboarding first (animation + sound on dashboard).
      flushPendingLevelUpCelebrations(currentUser.id)

      // Also check unread inbox in case FCM never queued (e.g. missed push).
      try {
        const inbox = await refreshNotificationInbox(dispatch, {
          userId: currentUser.id,
          // Dashboard flush owns onboarding level-ups — don't double-fire from inbox.
          skipLevelUpCelebration: true,
        })
        if (cancelled) return
        flushPendingLevelUpCelebrations(currentUser.id, {
          items: inbox?.items,
        })
      } catch {
        // non-blocking
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [currentUser?.id, dispatch])

  // Mandatory project-join modal: re-check on every visit so logout, refresh,
  // or reopening the invite email cannot bypass completing accept.
  useEffect(() => {
    let cancelled = false

    const resolveInvite = async () => {
      const fromState = location.state?.initialAcceptInvite
      if (fromState?.id && fromState?.projectId) {
        const alreadyDone = isMandatoryProjectAcceptDone(
          currentUser?.id,
          fromState.id,
        )
        if (!cancelled && !alreadyDone) {
          setInitialInvite(fromState)
        }
        navigate(location.pathname, { replace: true, state: {} })
      }

      const invite = await fetchMandatoryProjectAcceptInvite(currentUser)
      if (!cancelled) {
        setInitialInvite(invite)
      }
    }

    resolveInvite()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- remount / user change only
  }, [currentUser?.id, currentUser?.total_projects])

  useEffect(() => {
    let cancelled = false
    const fetchActive = async () => {
      const response = await get(endpoints.getProjects, {
        params: {
          page: 1,
          page_size: 7,
          status: "present",
          "with[]": ["categories", "roleTypes", "latest_members.user", "media"],
          sort_by: "created_at",
          sort_direction: "desc",
        },
      })
      if (!cancelled) {
        if (response?.data?.success) {
          setActiveProjects(
            (response.data.result?.data || []).map(mapApiProject),
          )
        }
        setIsLoading(false)
      }
    }
    fetchActive()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    const fetchCollaborators = async () => {
      // /api/v1/project-invitations?involvement=all&with[]=user&status=active&sort_by=created_at&distinct=1
      const response = await get(endpoints.getProjectInvitations, {
        params: {
          involvement: "all",
          status: "active",
          sort_by: "created_at",
          distinct: 1,
          page: 1,
          page_size: 20,
          "with[]": ["user", "roleTypes"],
        },
      })
      if (!cancelled && response?.data?.success) {
        const seen = new Set()
        const rows = []
        ;(response.data.result?.data || []).forEach(invitation => {
          const mapped = mapInvitationCollaborator(invitation, currentUser?.id)
          if (!mapped) return
          const dedupeKey = String(mapped.userId || mapped.id)
          if (seen.has(dedupeKey)) return
          seen.add(dedupeKey)
          rows.push(mapped)
        })
        setActiveCollaborators(rows.slice(0, 8))
      }
    }
    fetchCollaborators()
    return () => {
      cancelled = true
    }
  }, [currentUser?.id])

  useEffect(() => {
    let cancelled = false
    const fetchMe = async () => {
      const response = await get(endpoints.getMe, {
        params: { with: "addresses.city" },
      })
      if (!cancelled && response?.data?.success) {
        dispatch(setUser(response.data.result))
      }
    }
    fetchMe()
    return () => {
      cancelled = true
    }
  }, [dispatch])

  useEffect(() => {
    let cancelled = false
    const fetchSummary = async () => {
      const response = await get(endpoints.getInvitesSummary)
      if (!cancelled && response?.data?.success) {
        setInvitesSummary(response.data.result)
      }
    }
    fetchSummary()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    const fetchDashboardStats = async () => {
      const response = await get(endpoints.getDashboardStats, {
        params: { "types[]": "total_hours_log" },
      })
      if (!cancelled && response?.data?.success) {
        setDashboardStats(response.data.result?.total_hours_log)
      }
    }
    fetchDashboardStats()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    const fetchTasks = async () => {
      const response = await get(endpoints.getTasks, {
        params: {
          status: "active",
          page_size: 3,
          sort_by: "start_date",
          sort_direction: "desc",
          "with[]": ["schedules", "assignee", "collaborators.user", "project"],
        },
      })
      if (!cancelled && response?.data?.success) {
        setTodayTasks((response.data.result?.data || []).slice(0, 3))
      }
    }
    fetchTasks()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    const fetchSessions = async () => {
      const response = await get(endpoints.getSessions, {
        params: {
          sort_by: "started_at",
          sort_direction: "desc",
          page_size: 10,
          "with[]": [
            "targets",
            "targets.targetable",
            "collaborators.user",
            "creator",
          ],
        },
      })
      if (!cancelled && response?.data?.success) {
        const liveSessions = (response.data.result?.data || [])
          .filter(isLiveSession)
          .slice(0, 2)
        setActiveSessions(liveSessions)
      }
    }
    fetchSessions()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    const fetchProjectsSummary = async () => {
      const response = await get(endpoints.getProjectsSummary)
      if (!cancelled && response?.data?.success) {
        setProjectsSummary(response.data.result)
      }
    }
    fetchProjectsSummary()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    const fetchRecentActivity = async () => {
      try {
        const response = await get(endpoints.getMyActivityLogs, {
          params: {
            page: 1,
            page_size: 4,
          },
        })
        if (!cancelled && response?.data?.success) {
          const rows = (response.data.result?.data || [])
            .slice(0, 4)
            .map(mapActivityLog)
          setRecentActivity(rows)
        }
      } catch {
        if (!cancelled) {
          setRecentActivity([])
        }
      }
    }
    fetchRecentActivity()
    return () => {
      cancelled = true
    }
  }, [])

  const addresses = currentUser?.addresses || []
  const getAddressByType = type =>
    addresses.find(address => address?.address_type === type) || null
  const formatCityName = address => {
    const city = address?.city
    if (!city) return "--"
    if (typeof city === "string") return city
    const name = city.city || city.name || ""
    const state = city.state || ""
    if (name && state) return `${name}, ${state}`
    return name || "--"
  }
  const primaryCity = formatCityName(getAddressByType("primary"))
  const secondaryCity = formatCityName(getAddressByType("secondary"))
  const tertiaryCity = formatCityName(getAddressByType("tertiary"))
  const { xpValue, xpLevel, xpMax } = getXpProgress(currentUser)
  const inviteTopRight = invitesSummary?.has_unlimited_invites
    ? "Unlimited"
    : (invitesSummary?.total_invites_quota_remaining ?? 0)
  const pendingInvites = invitesSummary?.total_pending || 0
  const acceptedInvites = invitesSummary?.total_accepted || 0
  const cancelledInvites = invitesSummary?.total_cancelled || 0
  const hoursLogged = formatDurationFromHours(dashboardStats?.total_hours_log)
  const hoursProgress = dashboardStats?.todays_progress || "+0%"
  const hoursChartValues =
    dashboardStats?.weekly_percentage?.current_week?.map(d => d.hours) || []
  const mapSessionRow = session => {
    const project = resolveSessionProject(session)
    const city = resolveSessionCity(session)
    return {
      id: session.id,
      title: session?.type ? `${capitalize(session.type)} Session` : "Session",
      subtitle: resolveSessionSubtitle(session),
      badge: session?.visibility_type
        ? formatSessionVisibilityLabel(session.visibility_type)
        : "",
      note: session?.working_note || session?.title || "",
      projectId: project?.id || null,
      cityId: city?.id || null,
      city: city
        ? {
            id: city.id,
            name: city.name,
            title: city.name,
            shortName: city.name,
          }
        : null,
      project: project
        ? {
            id: project.id,
            name: project.name,
            title: project.name,
          }
        : null,
      canJoin: canJoinDashboardSession(session),
      people:
        session?.collaborators?.map(c => ({
          name:
            c.user?.full_name ||
            `${c.user?.first_name || ""} ${c.user?.last_name || ""}`.trim(),
          src: getUserAvatarUrl(c.user),
        })) || [],
    }
  }
  const presentProjects = Number(projectsSummary?.present_count) || 0
  const pastProjects = Number(projectsSummary?.past_count) || 0
  const futureProjects = Number(projectsSummary?.future_count) || 0
  const totalProjects = presentProjects + pastProjects + futureProjects

  const openProject = row => navigate(`/projects/${row.id}/overview`)

  const sessionCollaborators = useMemo(
    () =>
      getProjectCollaborators(sessionProject, currentUser, {
        excludeCurrentUser: true,
      }),
    [sessionProject, currentUser],
  )

  const closeSessionModal = () => {
    if (isCreatingSession) return
    setSessionModal(null)
    setSessionProject(null)
  }

  const handleStartSessionSelect = type => {
    if (!activeProjects.length) {
      toast.info("Create a project first to start a session.", {
        position: "bottom-center",
        theme: "colored",
        transition: Bounce,
      })
      navigate("/projects/create")
      return
    }
    setSessionProject(activeProjects[0])
    setSessionModal(type)
  }

  const openDashboardSessionDetail = row => {
    openSessionDetail({
      sessionId: row.id,
      projectId: row.projectId || null,
      cityId: row.cityId || null,
      city: row.city,
      returnTo: "/dashboard",
      returnLabel: "Returning to dashboard...",
    })
  }

  const handleOpenDashboardSession = row => {
    if (!row?.id) {
      if (row?.projectId) {
        navigate(`/projects/${row.projectId}/sessions`)
      }
      return
    }

    const activeSessionId = getActiveSessionSnapshot().sessionId
    if (activeSessionId && String(row.id) !== String(activeSessionId)) {
      toast.error(
        "You’re already in a session. Leave your current session to join another.",
        {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        },
      )
      return
    }

    // Invited but not joined — same as session invite notifications:
    // navigate to the sessions listing with openJoinSessionId.
    if (row.canJoin) {
      const joinState = { openJoinSessionId: String(row.id) }
      if (row.projectId) {
        navigate(`/projects/${row.projectId}/sessions`, { state: joinState })
        return
      }
      if (row.cityId) {
        navigate(cityHubTabPath("shadow-working-events", row.cityId), {
          state: joinState,
        })
        return
      }
    }

    openDashboardSessionDetail(row)
  }

  const createDashboardSession = async ({ type, note, userIds = [] }) => {
    const projectId = sessionProject?.id
    if (!projectId || isCreatingSession) return null

    setIsCreatingSession(true)
    try {
      const result = await createProjectSession({
        type,
        projectId,
        note,
        userIds,
        sessionTarget: "project",
        targetIds: [projectId],
      })

      if (!result.ok) {
        if (result.reason === "empty_note") {
          toast.error(
            "Please add a working note before starting the session.",
            {
              position: "bottom-center",
              theme: "colored",
              transition: Bounce,
            },
          )
        } else if (result.response) {
          toast.error(
            result.response?.data?.message || "Failed to create session",
            {
              position: "bottom-center",
              theme: "colored",
              transition: Bounce,
            },
          )
        }
        return null
      }

      toast.success(
        type === "solo" ? "Solo session created." : "Shadow session created.",
        {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        },
      )
      setSessionModal(null)
      setSessionProject(null)
      if (result.created?.id) {
        openSessionDetail({
          sessionId: result.created.id,
          projectId,
          project: sessionProject,
          returnTo: "/dashboard",
          returnLabel: "Returning to dashboard...",
        })
      }
      return result.created
    } catch (err) {
      if (err?.response || err?.code) return null
      toast.error(err?.message || "Failed to create session", {
        position: "bottom-center",
        theme: "colored",
        transition: Bounce,
      })
      return null
    } finally {
      setIsCreatingSession(false)
    }
  }

  const greetingTitle = useMemo(
    () => formatDashboardGreeting(currentUser?.first_name),
    [currentUser?.first_name],
  )

  return (
    <div className="page-content">
      <div className="dashboard" aria-busy={isLoading || undefined}>
        <PageHeader
          className="dashboard__header"
          title={greetingTitle}
          subtitle="Glad you're here! What are you going to work on today?"
          actions={
            <>
              <StartSessionDropdown
                variant="secondary"
                size="md"
                playIconSrc={ICONS.play}
                onSelect={handleStartSessionSelect}
              />
              <CustomButton
                variant="primary"
                size="md"
                type="button"
                trailingIconSrc={ICONS.plus}
                trailingIconAlt=""
                onClick={() => navigate("/projects/create")}
              >
                Create Project
              </CustomButton>
            </>
          }
        />

        {isLoading ? (
          <>
            <div className="dashboard__grid dashboard__grid--4">
              {Array.from({ length: 4 }).map((_, i) => (
                <DashboardSkeletonCard key={`kpi-skel-${i}`} />
              ))}
            </div>
            <div className="dashboard__main">
              <div className="dashboard__widgets">
                {Array.from({ length: 3 }).map((_, i) => (
                  <DashboardSkeletonCard key={`widget-skel-${i}`} />
                ))}
              </div>
              <DashboardSkeletonCard className="dashboard__activity" />
              <DashboardSkeletonCard className="dashboard__projects-card dashboard__skeleton-card--tall" />
              <DashboardSkeletonCard className="dashboard__collaborators dashboard__skeleton-card--tall" />
            </div>
          </>
        ) : (
          <>
            <div className="dashboard__grid dashboard__grid--4">
              <DashboardCard
                title="City"
                tone="red"
                topRight={primaryCity}
                fullHeight
              >
                <MiniStatRow>
                  <MiniStat label="Primary" value={primaryCity} color="red" />
                  <MiniStat
                    label="Secondary"
                    value={secondaryCity}
                    color="red"
                  />
                  <MiniStat label="Tertiary" value={tertiaryCity} color="red" />
                </MiniStatRow>
                <CustomButton
                  variant="transparent"
                  size="sm"
                  type="button"
                  fullWidth
                  trailingIconSrc={ICONS.refresh}
                  trailingIconAlt=""
                  onClick={() => setIsCityRequestOpen(true)}
                >
                  Request Change City
                </CustomButton>
              </DashboardCard>

              <DashboardCard
                title="Total Projects"
                tone="darkgreen"
                topRight={totalProjects}
                fullHeight
              >
                <MiniStatRow>
                  <MiniStat
                    label="Active"
                    value={presentProjects}
                    color="darkgreen"
                  />
                  <MiniStat
                    label="Past"
                    value={pastProjects}
                    color="darkgreen"
                  />
                  <MiniStat
                    label="Future"
                    value={futureProjects}
                    color="darkgreen"
                  />
                </MiniStatRow>
                <CustomButton
                  variant="transparent"
                  size="sm"
                  type="button"
                  fullWidth
                  trailingIconSrc={ICONS.plus}
                  trailingIconAlt=""
                  onClick={() => navigate("/projects/create")}
                >
                  Create New Project
                </CustomButton>
              </DashboardCard>

              <DashboardCard
                title="XP Earned"
                tone="purple-custom"
                topRight={`Level ${xpLevel}`}
                fullHeight
              >
                <div className="dashboard__xp-row">
                  <p className="dashboard__xp-value">{`${xpValue}xp`}</p>
                  <p className="dashboard__xp-max">{`${xpMax}xp`}</p>
                </div>
                <ProgressBar value={xpValue} max={xpMax} tone="purple" />
              </DashboardCard>

              <DashboardCard
                title="Invites Available"
                tone="darkblue"
                topRight={inviteTopRight}
                fullHeight
              >
                <MiniStatRow>
                  <MiniStat
                    label="Pending"
                    value={pendingInvites}
                    color="darkblue"
                  />
                  <MiniStat
                    label="Accepted"
                    value={acceptedInvites}
                    color="darkblue"
                  />
                  <MiniStat
                    label="Declined"
                    value={cancelledInvites}
                    color="darkblue"
                  />
                </MiniStatRow>
                <CustomButton
                  variant="transparent"
                  size="sm"
                  type="button"
                  fullWidth
                  trailingIconSrc={ICONS.send}
                  trailingIconAlt=""
                  onClick={() => setIsInviteOpen(true)}
                >
                  Invite Collaborators
                </CustomButton>
              </DashboardCard>
            </div>

            <div className="dashboard__main">
              <div className="dashboard__widgets">
                <DashboardCard
                  title="Today's Primary Focus"
                  tone="brown"
                  fullHeight
                >
                  {todayTasks.length === 0 ? (
                    <EmptyState
                      size="sm"
                      title="No focus tasks yet"
                      description="Tasks due today will show up here as your primary focus."
                    />
                  ) : (
                    todayTasks.map(task => {
                      const collaborators =
                        task.collaborators?.map(c => ({
                          name:
                            c.user?.full_name ||
                            `${c.user?.first_name || ""} ${c.user?.last_name || ""}`.trim(),
                          src: getUserAvatarUrl(c.user),
                        })) || []
                      const projectId = task.project?.id
                      const openTask = () => {
                        if (!projectId) return
                        navigate(`/projects/${projectId}/my-tasks`)
                      }
                      return (
                        <ListItem
                          key={task.id}
                          leadingIcon={ICONS.focusTask}
                          title={task.title}
                          subtitle={task.project?.name || ""}
                          trailing={
                            collaborators.length > 0 ? (
                              <AvatarGroup people={collaborators} size="xs" />
                            ) : null
                          }
                          className="list-item--focus"
                          bordered
                          onClick={
                            projectId && currentUser?.id ? openTask : undefined
                          }
                        />
                      )
                    })
                  )}
                </DashboardCard>

                <DashboardCard
                  title="Active Session"
                  tone="orange-custom"
                  fullHeight
                >
                  {activeSessions.length === 0 ? (
                    <EmptyState
                      size="sm"
                      title="No active session"
                      description="Start a solo or shadow session to see it here."
                    />
                  ) : (
                    <div className="dashboard__session-list">
                      {activeSessions.map(session => {
                        const row = mapSessionRow(session)
                        return (
                          <button
                            key={row.id}
                            type="button"
                            className="dashboard__session-layout dashboard__session-layout--clickable"
                            onClick={() => handleOpenDashboardSession(row)}
                            aria-label={`Open ${row.title}`}
                          >
                            <div className="dashboard__session-top">
                              <div className="dashboard__session-info">
                                <img
                                  src={ICONS.session}
                                  alt=""
                                  className="dashboard__session-icon"
                                />
                                <div className="dashboard__session-copy">
                                  <h4 className="dashboard__session-title">
                                    {row.title}
                                  </h4>
                                  <p
                                    className="dashboard__session-subtitle"
                                    title={row.subtitle || undefined}
                                  >
                                    {row.subtitle}
                                  </p>
                                </div>
                              </div>
                              {row.badge ? (
                                <span className="list-item__badge">
                                  {row.badge}
                                </span>
                              ) : null}
                            </div>
                            <div className="dashboard__session-bottom">
                              <p
                                className="dashboard__session-note"
                                title={row.note || undefined}
                              >
                                {row.note}
                              </p>
                              <AvatarGroup people={row.people} size="xs" />
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  )}
                </DashboardCard>

                <DashboardCard
                  title="Total Hours Logged"
                  tone="lightblue-custom"
                  topRight={hoursProgress}
                  fullHeight
                >
                  <div className="dashboard__hours-row">
                    <p className="dashboard__hours-value">{hoursLogged}</p>
                    <MiniBarChart values={hoursChartValues} tone="blue" />
                  </div>
                </DashboardCard>
              </div>

              <DashboardCard
                title="Recent Activity"
                tone="lightteal"
                className="dashboard__activity"
                fullHeight
                topRight={
                  <CustomButton
                    variant="primary"
                    size="sm"
                    type="button"
                    trailingIconSrc={ICONS.arrowRight}
                    trailingIconAlt=""
                    onClick={() => navigate("/activity-log")}
                  >
                    View All
                  </CustomButton>
                }
              >
                {recentActivity.length === 0 ? (
                  <EmptyState
                    size="sm"
                    title="No recent activity"
                    description="Your latest updates and actions will appear here."
                  />
                ) : (
                  recentActivity.map(item => (
                    <ListItem
                      key={item.id}
                      dotColor={item.dotColor}
                      title={item.title}
                      subtitle={item.time}
                      bordered
                    />
                  ))
                )}
              </DashboardCard>

              <DashboardCard
                title="Active Projects"
                className="dcard--no-spacing dcard--no-justify dashboard__projects-card"
                fullHeight
                topRight={
                  <CustomButton
                    variant="primary"
                    size="sm"
                    type="button"
                    trailingIconSrc={ICONS.arrowRight}
                    trailingIconAlt=""
                    onClick={() => navigate("/projects")}
                  >
                    View All
                  </CustomButton>
                }
              >
                <div className="dashboard__projects-wrap">
                  <ProjectsTable
                    rows={activeProjects}
                    arrowIconSrc={ICONS.arrowRightBlack}
                    onViewDetail={openProject}
                  />
                </div>
              </DashboardCard>

              <DashboardCard
                title="Active Collaborators"
                tone="pinkbrown"
                className="dcard--no-justify dashboard__collaborators"
                fullHeight
                topRight={
                  <CustomButton
                    variant="primary"
                    size="sm"
                    type="button"
                    trailingIconSrc={ICONS.arrowRight}
                    trailingIconAlt=""
                    onClick={() => navigate("/collaborators")}
                  >
                    View All
                  </CustomButton>
                }
              >
                {activeCollaborators.length === 0 ? (
                  <EmptyState
                    size="sm"
                    title="No active collaborators"
                    description="People you collaborate with will show up here."
                  />
                ) : (
                  <div className="dashboard__collaborators-list">
                    {activeCollaborators.map(person => (
                      <ListItem
                        key={person.id}
                        avatarName={person.name}
                        avatarSrc={person.avatar}
                        title={person.name}
                        trailing={
                          person.role ? (
                            <span className="dashboard__role">
                              {person.role}
                            </span>
                          ) : null
                        }
                        className="list-item--collaborator"
                        bordered
                      />
                    ))}
                  </div>
                )}
              </DashboardCard>
            </div>
          </>
        )}
      </div>

      <RequestChangeCityModal
        isOpen={isCityRequestOpen}
        onClose={() => setIsCityRequestOpen(false)}
        addresses={addresses}
      />

      <AddCollaboratorModal
        isOpen={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
        showProjectSelect
        title="Invite Collaborator"
        subtitle="Select a project and invite collaborators to join."
        onInvited={async () => {
          const response = await get(endpoints.getInvitesSummary)
          if (response?.data?.success) {
            setInvitesSummary(response.data.result)
          }
        }}
      />

      <StartSoloSessionModal
        isOpen={sessionModal === "solo"}
        onClose={closeSessionModal}
        project={sessionProject}
        projectOptions={activeProjects}
        onProjectChange={setSessionProject}
        onStart={({ note }) =>
          createDashboardSession({ type: "solo", note, userIds: [] })
        }
        isSubmitting={isCreatingSession}
      />

      <StartShadowSessionModal
        isOpen={sessionModal === "shadow"}
        onClose={closeSessionModal}
        project={sessionProject}
        projectOptions={activeProjects}
        onProjectChange={setSessionProject}
        collaborators={sessionCollaborators}
        onStart={({ note, userIds }) =>
          createDashboardSession({ type: "shadow", note, userIds })
        }
        isSubmitting={isCreatingSession}
      />

      <AcceptInviteModal
        isOpen={Boolean(initialInvite)}
        onClose={() => {
          // Mandatory until API says join profile is complete — ignore dismiss.
        }}
        invite={initialInvite}
        isMandatory
        useProjectMemberAccept
        onSuccess={async () => {
          const projectId = initialInvite?.projectId
          const membershipId = initialInvite?.id
          // Mark done before clearing / navigating so middleware won't bounce
          // back to dashboard and briefly reopen this modal.
          markMandatoryProjectAcceptDone(currentUser?.id, membershipId)
          setInitialInvite(null)
          if (projectId) {
            navigate(`/projects/${projectId}`, { replace: true })
          }
        }}
      />
    </div>
  )
}

export default Dashboard
